import { User, Shirt, ImageIcon, Sun, Camera, Mountain } from 'lucide-react';

import { FormSection } from '@/components/ui/form-section';
import { LabeledTextarea } from '@/components/ui/labeled-textarea';
import { ReferenceCard } from '@/components/ui/reference-card';
import { Label } from '@/components/ui/label';

import { VisualSelectTrigger } from './VisualSelectTrigger';

import type { IPhotostudioFormColumnProps } from '@/@types/photostudio';

export function PhotostudioFormColumn(props: IPhotostudioFormColumnProps) {
  const {
    subjectAction,
    setSubjectAction,
    environment,
    setEnvironment,
    shotType,
    lightingSource,
    photographyGenre,
    atmosphere,
    setAtmosphere,
    cameraBody,
    focalLength,
    lensType,
    filmStock,
    isGenerating,
    personPreviewUrl,
    outfitPreviewUrl,
    scenePreviewUrl,
    characterSlot,
    outfitSlot,
    sceneSlot,
    onOpenPersonPicker,
    onOpenElementPicker,
    onClearPerson,
    onClearOutfit,
    onClearScene,
    setVisualPickerOpen,
  } = props;

  return (
    <div className="w-[360px] shrink-0 space-y-5 overflow-y-auto">
      <FormSection number="01" title="Subject & Framing" icon={User} sectionColor="1">
        <LabeledTextarea
          label="Subject & Action"
          value={subjectAction}
          onChange={setSubjectAction}
          placeholder="Describe the subject and their action..."
          rows={3}
        />
        <LabeledTextarea
          label="Environment"
          value={environment}
          onChange={setEnvironment}
          placeholder="Describe the setting or background..."
          rows={3}
        />
        <div className="space-y-1.5">
          <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Shot Type / Angle
          </Label>
          <VisualSelectTrigger
            value={shotType}
            placeholder="Select shot type..."
            onClick={() => setVisualPickerOpen('shotType')}
            disabled={isGenerating}
            category="shot-types"
          />
        </div>
      </FormSection>

      <FormSection title="References" icon={ImageIcon} sectionColor="references">
        <div className="grid grid-cols-3 items-start gap-2.5">
          <ReferenceCard
            label="Person"
            icon={User}
            imageUrl={personPreviewUrl}
            onClick={onOpenPersonPicker}
            onClear={characterSlot.type !== 'empty' ? onClearPerson : undefined}
            disabled={isGenerating}
          />
          <ReferenceCard
            label="Outfit"
            icon={Shirt}
            imageUrl={outfitPreviewUrl}
            onClick={() => onOpenElementPicker('outfit')}
            onClear={outfitSlot.type !== 'empty' ? onClearOutfit : undefined}
            disabled={isGenerating}
          />
          <ReferenceCard
            label="Scene"
            icon={Mountain}
            imageUrl={scenePreviewUrl}
            onClick={() => onOpenElementPicker('scene')}
            onClear={sceneSlot.type !== 'empty' ? onClearScene : undefined}
            disabled={isGenerating}
          />
        </div>
      </FormSection>

      <FormSection number="02" title="Mood & Style" icon={Sun} sectionColor="2">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Lighting Source
            </Label>
            <VisualSelectTrigger
              value={lightingSource}
              placeholder="Select lighting..."
              onClick={() => setVisualPickerOpen('lighting')}
              disabled={isGenerating}
              category="lighting"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Photography Genre
            </Label>
            <VisualSelectTrigger
              value={photographyGenre}
              placeholder="Select genre..."
              onClick={() => setVisualPickerOpen('photographyGenre')}
              disabled={isGenerating}
              category="photography-genre"
            />
          </div>
        </div>
        <LabeledTextarea
          label="Atmosphere / Mood"
          value={atmosphere}
          onChange={setAtmosphere}
          placeholder="Describe the mood or atmosphere..."
          rows={2}
        />
      </FormSection>

      <FormSection number="03" title="Camera Gear" icon={Camera} sectionColor="3">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Camera Body
            </Label>
            <VisualSelectTrigger
              value={cameraBody}
              placeholder="Select camera..."
              onClick={() => setVisualPickerOpen('cameraBody')}
              disabled={isGenerating}
              category="camera-body"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Focal Length
            </Label>
            <VisualSelectTrigger
              value={focalLength}
              placeholder="Select focal length..."
              onClick={() => setVisualPickerOpen('focalLength')}
              disabled={isGenerating}
              category="focal-length"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Lens Type
            </Label>
            <VisualSelectTrigger
              value={lensType}
              placeholder="Select lens type..."
              onClick={() => setVisualPickerOpen('lensType')}
              disabled={isGenerating}
              category="lens"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Film Stock
            </Label>
            <VisualSelectTrigger
              value={filmStock}
              placeholder="Select film stock..."
              onClick={() => setVisualPickerOpen('filmStock')}
              disabled={isGenerating}
              category="film-stock"
            />
          </div>
        </div>
      </FormSection>
    </div>
  );
}
